import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { OnChangeFn, Row, SortingState } from '@tanstack/react-table';

import { useGetAmendmentsQuery } from '@xemplo/amendment-query';
import { builSortParams } from '@xemplo/gp-api';
import { AmendmentListItem } from '@xemplo/gp-types';
import { useAmendmentDetailDrawer } from '@xemplo/payrun-amendment-detail';
import { useServerSideState } from '@xemplo/table';

import { columns } from './payrun-amendments-list.helper';
import * as S from './payrun-amendments-list.style';
import { PayrunAmendmentsListProps } from './payrun-amendments-list.types';

export function PayrunAmendmentsList({
  onAmendmentSelect,
  className,
}: Readonly<PayrunAmendmentsListProps>) {
  const { id } = useParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { pagination, onPaginationChange } = useServerSideState({
    pageIndex: 0,
    pageSize: 10,
  });
  const { pageIndex, pageSize } = pagination;
  const { data, isLoading } = useGetAmendmentsQuery({
    requestParams: {
      page: pageIndex + 1,
      per_page: pageSize,
      filter: { payrunId: id },
      ...builSortParams(sorting),
    },
  });

  const { updateDrawer } = useAmendmentDetailDrawer();

  const handleAmendmentSelect = (
    e: React.MouseEvent<Element, MouseEvent>,
    row: Row<AmendmentListItem>,
  ) => {
    const target = e.target as HTMLElement;
    // If the click is on the checkbox, don't toggle the drawer
    if (target?.getAttribute('data-role') === 'checkbox') return;
    updateDrawer(row);
    onAmendmentSelect?.(e, row);
  };

  const handleSortingChange: OnChangeFn<SortingState> = (sorting) => {
    setSorting(sorting);
  };

  return (
    <S.Container className={className}>
      <S.Table
        data={data?.result.rows ?? []}
        columns={columns}
        enablePagination
        manualPagination
        isListTable
        enableRowSelection={false}
        pageCount={data?.result.total_Pages ?? 0}
        state={{ pagination, sorting }}
        onPaginationChange={onPaginationChange}
        onBodyRowClick={handleAmendmentSelect}
        onSortingChange={handleSortingChange}
        totalRowCount={data?.result.total ?? 0}
        isLoading={isLoading}
      />
    </S.Container>
  );
}

export default PayrunAmendmentsList;
